// import { BankrupcyEffect } from "./Effects";
import { GameEvents } from "../utils/Types";
import { Global } from "../utils/Global";
import { Effect, BankruptcyEffect } from "../inventory/Effects";
import { WheelSection, WheelSectionColors } from "./WheelSection";
import { WheelPointer } from "./WheelPointer";
import { DropZoneShape, ItemDropZone } from "../utils/ItemDropZone";
import { ItemType, SlotType } from "../inventory/BaseItem";
import Client from "..";
import { WheelSectionDescriptor } from "../gui/windows/WheelSectionDescriptor";
import { Phase } from "../utils/StateMachine";

export class Wheel extends Phaser.GameObjects.Container {
  public sections: WheelSection[] = [];
  public minNumberOfSections = 3;
  public maxNumberOfSections = 25;
  public minSectorSize = 10; // All sections need at least minSectorSize°
  public pointer: WheelPointer;
  public radius: number = 1;

  public locked = false;

  private minSpinForce = 600;
  private maxSpinForce = 800;
  private minSpinVelocity = 0.1; // minimum velocity to consider "spinning" (degrees/s)
  private angularDrag = 10;
  private physicsBody?: Phaser.Physics.Arcade.Body;

  // Pegs
  private pegHitSfx: Phaser.Sound.BaseSound;
  private pegAngles: number[] = []; // degrees from 12 o'clock, clockwise
  private hitPegs: Set<number> = new Set(); // track which peg angles have been hit this full rotation
  private pegDragFactor: number = 0.95;

  private lastRotation = 0;
  private totalRotationDistance = 0;

  // Animation system
  private isAnimating = false;
  private activeTweens: Phaser.Tweens.Tween[] = [];
  private animationDuration = 600; // milliseconds

  // Section descriptor
  private sectionDescriptor?: WheelSectionDescriptor;

  // Brake system
  private isBraking = false;
  private brakeUsage = 0; // 0-100, percentage of brake usage
  private maxBrakeUsage = 100;
  private brakeUsagePerFrame = 1; // How much brake usage increases per frame of braking
  private brakePower = 3;

  // Drop zone
  private dropZone!: ItemDropZone<ItemType | SlotType>;

  // Debug stuff
  private debugRotationSpeed = 2; // degrees per frame when using arrow keys
  private leftKey?: Phaser.Input.Keyboard.Key;
  private rightKey?: Phaser.Input.Keyboard.Key;
  private xKey?: Phaser.Input.Keyboard.Key; // Remove section
  private yKey?: Phaser.Input.Keyboard.Key; // Add section
  private debugGraphics?: Phaser.GameObjects.Graphics; // Store reference to debug graphics

  constructor(scene: Phaser.Scene) {
    super(scene);

    // if you load game here then load wheel from save file

    // otherwise you make a default wheel on new game
    if (this.sections.length === 0) {
      this.createStarterFields();
    }

    // prepare the sounds
    this.pegHitSfx = scene.sound.add("sfx_wheelPegHit");

    this.pointer = new WheelPointer(scene, this);

    // Create section descriptor
    this.sectionDescriptor = new WheelSectionDescriptor(scene);
    scene.add.existing(this.sectionDescriptor);

    // Setup physics body
    scene.physics.add.existing(this);
    this.physicsBody = this.body as Phaser.Physics.Arcade.Body;
    this.physicsBody.setCircle(this.radius);
    this.physicsBody.setAngularDrag(this.angularDrag); // Lower drag for longer spins

    // Setup drop zone
    this.dropZone = new ItemDropZone(
      this.scene,
      [ItemType.CONSUMABLE],
      this.OnZoneEnter.bind(this),
      this.OnZoneLeave.bind(this),
      this.OnZoneDrop.bind(this),
    );
    this.add(this.dropZone);

    // If debug allow manual rotation and setup controls
    if (Global.debug) {
      this.setupDebugControls();
    }

    scene.events.on(Phaser.Scenes.Events.UPDATE, this.update, this); // tell the class its updated each frame (graphics and physics)
    scene.game.events.on(GameEvents.SPIN_WHEEL, this.spin, this);
    scene.game.events.on(GameEvents.BREAK_WHEEL_ON, this.startBraking, this);
    scene.game.events.on(GameEvents.BREAK_WHEEL_OFF, this.stopBraking, this);
  }

  Resize(radius: number): void {
    this.radius = radius;
    this.setSize(radius * 2, radius * 2);

    this.drawSections();
    this.pointer.draw();

    this.dropZone.SetShape(DropZoneShape.CIRCLE, radius);

    // Update physics body radius
    if (this.physicsBody) {
      this.physicsBody.setCircle(this.radius);
    }
  }

  /**
   * Update debug graphics - should be called after position is set
   */
  public updateDebugGraphics(): void {
    if (Global.debug) {
      this.drawDetectionPointLine();
    }
  }

  private spin(): void {
    Client.state.wheelSpinning = true;
    this.lockWheel(false); // Unlock the wheel for spinning

    // Force end all hover animations on sections
    for (const section of this.sections) {
      section.forceEndHover();
    }

    // Hide section descriptor immediately when spinning starts
    this.hideSectionDescriptorImmediately();

    this.recomputePegs();
    this.hitPegs.clear();

    // Reset rotation tracking
    this.lastRotation = this.rotation;
    this.totalRotationDistance = 0;

    this.isBraking = false;

    // Apply initial angular velocity using Phaser physics - positive for counterclockwise rotation only
    if (this.physicsBody) {
      this.physicsBody.setAngularVelocity(
        Math.abs(
          Phaser.Math.FloatBetween(this.minSpinForce, this.maxSpinForce),
        ),
      );
    }
  }

  override update(_time: number, _delta: number) {
    // Debug controls can break the lock and work when not spinning
    if (Global.debug && !Client.state.wheelSpinning) {
      if (this.isDebugRotating()) {
        // Debug mode can unlock the wheel
        this.lockWheel(false);
        this.handleDebugControls();
      } else {
        // No debug keys pressed - re-lock the wheel if it was unlocked by debug
        this.lockWheel(true);
      }
    }

    if (!this.physicsBody || !Client.state.wheelSpinning || this.locked) return;

    // Ensure wheel only rotates counterclockwise during actual gameplay (not debug)
    if (this.physicsBody.angularVelocity < 0) {
      this.physicsBody.setAngularVelocity(0);
    }

    // Handle braking
    if (this.isBraking && this.brakeUsage < this.maxBrakeUsage) {
      this.applyBrake();
      this.brakeUsage += this.brakeUsagePerFrame;
      if (this.brakeUsage > this.maxBrakeUsage) {
        this.brakeUsage = this.maxBrakeUsage;
      }
    }

    // Track rotation distance for peg hit reset
    const rotationDelta = this.rotation - this.lastRotation;
    this.totalRotationDistance += Math.abs(rotationDelta);
    this.lastRotation = this.rotation;

    // Reset peg hits every 2π radians (360°)
    if (this.totalRotationDistance >= Math.PI * 2) {
      this.hitPegs.clear();
      this.totalRotationDistance = 0;
    }

    // Eather its too slow to spin and we stop or we process pegs
    if (Math.abs(this.physicsBody.angularVelocity) < this.minSpinVelocity) {
      this.finishSpin();
    } else {
      this.handlePegTicks(
        (((-90 - Phaser.Math.RadToDeg(this.rotation)) % 360) + 360) % 360,
      );
    }
  }

  public addSection(
    score: number = 10,
    fieldSize: number = 30,
    fieldColor: WheelSectionColors = WheelSectionColors.RED,
    text: string | null = null,
    _effects: Effect[] = [],
    pegs: number = 1,
    position?: number, // Optional position to insert at (default: end)
    animated: boolean = true, // Whether to animate the addition
  ): void {
    if (Client.state.wheelSpinning || this.isAnimating) return; // Don't allow changes while spinning or animating

    // Check maximum sections limit
    if (this.sections.length >= this.maxNumberOfSections) {
      console.warn(
        `Cannot add section: Maximum of ${this.maxNumberOfSections} sections reached`,
      );
      return;
    }

    // Check if we can fit a new section
    const minSizeNeeded = (this.sections.length + 1) * this.minSectorSize;
    if (minSizeNeeded > 360) {
      console.warn(
        `Cannot add section: Would require ${minSizeNeeded}° but wheel only has 360°`,
      );
      return;
    }

    // Redistribute space among all sections (including the new one)
    if (this.sections.length > 0) {
      const targetNewSectionSize = Math.max(this.minSectorSize, fieldSize); // Ensure minimum minSectorSize°

      if (Global.debug) {
        const currentTotalSize = this.sections.reduce(
          (sum, section) => sum + section.fieldSize,
          0,
        );
        console.log(
          `Adding section: Current total=${currentTotalSize}°, New section=${targetNewSectionSize}°, Total sections will be=${this.sections.length + 1}`,
        );
      }

      // Check if we can fit all sections with minimum sizes
      const minTotalNeeded = (this.sections.length + 1) * this.minSectorSize;
      if (minTotalNeeded > 360) {
        console.warn(
          `Cannot add section: Would need ${minTotalNeeded}° for ${this.sections.length + 1} sections (minimum ${this.minSectorSize}° each)`,
        );
        return;
      }

      // Calculate how much space is available for distribution above minimum sizes
      const extraSpace = 360 - minTotalNeeded; // Space above minimum requirements
      const newSectionExtra = Math.max(
        0,
        targetNewSectionSize - this.minSectorSize,
      );
      const totalExtraNeeded =
        this.sections.reduce(
          (sum, section) =>
            sum + Math.max(0, section.fieldSize - this.minSectorSize),
          0,
        ) + newSectionExtra;

      if (totalExtraNeeded <= extraSpace) {
        // We can fit everything as requested
        fieldSize = targetNewSectionSize;
      } else {
        // Need to redistribute extra space proportionally
        const ratio = extraSpace / totalExtraNeeded;

        // Shrink existing sections proportionally
        for (const section of this.sections) {
          const currentExtra = Math.max(
            0,
            section.fieldSize - this.minSectorSize,
          );
          const newExtra = currentExtra * ratio;
          section.fieldSize = this.minSectorSize + newExtra;
        }

        // Set new section size proportionally
        const newSectionExtraScaled = newSectionExtra * ratio;
        fieldSize = this.minSectorSize + newSectionExtraScaled;

        if (Global.debug) {
          console.log(
            `Redistributed space: ratio=${ratio.toFixed(3)}, new section=${fieldSize.toFixed(1)}°`,
          );
        }
      }
    }

    // Create new section
    const newSection = new WheelSection(
      this,
      this.scene,
      score,
      fieldSize,
      fieldColor,
      text,
      [],
      pegs,
    );

    // Insert section at specified position or at the end
    if (
      position !== undefined &&
      position >= 0 &&
      position <= this.sections.length
    ) {
      this.sections.splice(position, 0, newSection);
    } else {
      this.sections.push(newSection);
    }
    this.add(newSection);

    // Normalize to ensure exactly 360° total (fix floating point errors)
    const currentTotal = this.sections.reduce(
      (sum, section) => sum + section.fieldSize,
      0,
    );
    if (Math.abs(currentTotal - 360) > 0.001) {
      // If not close enough to 360°
      const correctionFactor = 360 / currentTotal;
      for (const section of this.sections) {
        section.fieldSize *= correctionFactor;
      }

      if (Global.debug) {
        const finalTotal = this.sections.reduce(
          (sum, section) => sum + section.fieldSize,
          0,
        );
        console.log(
          `Normalized sizes: ${currentTotal.toFixed(3)}° → ${finalTotal.toFixed(3)}° (factor: ${correctionFactor.toFixed(6)})`,
        );
      }
    }

    // Final validation
    const finalTotalSize = this.sections.reduce(
      (sum, section) => sum + section.fieldSize,
      0,
    );
    if (Global.debug) {
      console.log(
        `Section added. Final total size: ${finalTotalSize.toFixed(3)}°`,
      );
    }

    if (animated) {
      this.animateAddSection(newSection, position || this.sections.length - 1);
    } else {
      // Redraw the wheel immediately
      this.drawSections();
      this.recomputePegs();
    }
  }

  /**
   * Skip all animations by completing them instantly
   */
  /*
  public skipAnimations(): void {
    for (const tween of this.activeTweens) {
      if (tween && !tween.isDestroyed()) {
        tween.complete();
      }
    }
  }
  */

  public removeSection(index?: number, animated: boolean = true): void {
    if (Client.state.phase === Phase.SPINNING || this.isAnimating) return; // Don't allow changes while spinning or animating

    // Check minimum sections limit
    if (this.sections.length <= this.minNumberOfSections) {
      console.warn(
        `Cannot remove section: Minimum of ${this.minNumberOfSections} sections required`,
      );
      return;
    }

    // Remove random section if no index specified
    const removeIndex =
      index !== undefined
        ? index
        : Math.floor(Math.random() * this.sections.length);

    if (removeIndex < 0 || removeIndex >= this.sections.length) return;

    const removedSection = this.sections[removeIndex];
    const removedSize = removedSection.fieldSize;

    if (animated) {
      this.animateRemoveSection(removedSection, removeIndex, removedSize);
    } else {
      // Remove the section immediately
      removedSection.destroy();
      this.sections.splice(removeIndex, 1);

      // Redistribute the removed size among remaining sections
      if (this.sections.length > 0) {
        const expandPerSection = removedSize / this.sections.length;

        for (const section of this.sections) {
          section.fieldSize += expandPerSection;
        }
      }

      // Redraw the wheel
      this.drawSections();
      this.recomputePegs();
    }
  }

  public getBrakeUsage(): number {
    return this.brakeUsage;
  }

  public getMaxBrakeUsage(): number {
    return this.maxBrakeUsage;
  }

  public getAngularVelocity(): number {
    return this.physicsBody?.angularVelocity || 0;
  }

  public getIsBraking(): boolean {
    return this.isBraking;
  }

  /* DropZoneHandlers */
  public OnZoneEnter(): void {
    // Visual feedback could be added here
    this.scene.tweens.killTweensOf(this);
    this.scene.tweens.add({
      targets: this,
      scale: 1.1,
      duration: 200,
      ease: "Power2",
    });
  }

  public OnZoneLeave(): void {
    // Visual feedback could be removed here
    this.scene.tweens.killTweensOf(this);
    this.scene.tweens.add({
      targets: this,
      scale: 1,
      duration: 200,
      ease: "Power2",
    });
  }

  public OnZoneDrop(effects?: Effect[]): boolean {
    // Visual feedback could be removed here
    this.scene.tweens.killTweensOf(this);
    this.scene.tweens.add({
      targets: this,
      scale: 1,
      duration: 200,
      ease: "Power2",
    });

    if (effects) {
      for (let i = 0; i < effects.length; i++) {
        if (!effects[i].OnUseEffect()) return false;
      }
    }

    return true;
  }

  /**
   * Reset brake usage to 0 (used when adding a new brake)
   */
  public resetBrakeUsage(): void {
    this.brakeUsage = 0;
  }

  /**
   * Disable all wheel interactions (hover, drop zones, etc.)
   */
  public disableAllInteractions(): void {
    // Disable hover interactions on all sections
    for (const section of this.sections) {
      if (section.input) {
        section.input.enabled = false;
      }
      // Force end any active hover effects
      section.forceEndHover();
    }

    // Hide section descriptor
    this.hideSectionDescriptorImmediately();
  }

  /**
   * Re-enable all wheel interactions (hover, drop zones, etc.)
   */
  public enableAllInteractions(): void {
    // Re-enable hover interactions on all sections
    for (const section of this.sections) {
      if (section.input) {
        section.input.enabled = true;
      }
    }
  }

  /**
   * Show section descriptor for a specific wheel section
   */
  public showSectionDescriptor(
    section: WheelSection,
    mouseX: number,
    mouseY: number,
  ): void {
    if (this.sectionDescriptor) {
      this.sectionDescriptor.showForSection(section, mouseX, mouseY);
    }
  }

  /**
   * Hide section descriptor
   */
  public hideSectionDescriptor(): void {
    if (this.sectionDescriptor) {
      this.sectionDescriptor.hideDescriptor();
    }
  }

  /**
   * Hide section descriptor immediately without delay
   */
  public hideSectionDescriptorImmediately(): void {
    if (this.sectionDescriptor) {
      this.sectionDescriptor.hideDescriptorImmediately();
    }
  }

  override destroy(fromScene?: boolean): void {
    this.scene.events.off(Phaser.Scenes.Events.UPDATE, this.update, this);

    // Cancel all animations before destroying
    this.cancelAllAnimations();

    if (this.physicsBody) {
      this.physicsBody.world.disableBody(this.physicsBody);
    }

    // Clean up debug graphics
    if (this.debugGraphics) {
      this.debugGraphics.destroy();
      this.debugGraphics = undefined;
    }

    super.destroy(fromScene);
  }

  /** Fire ticks for each peg crossed based on current position */
  private handlePegTicks(currentLandingDeg: number) {
    if (this.pegAngles.length === 0) return;

    // Get current angular velocity from physics body
    const currentAngularVelocity = this.physicsBody?.angularVelocity || 0;

    // Only process hits if we're actually rotating with significant movement
    if (Math.abs(currentAngularVelocity) < 0.1) return;

    const isClockwise = currentAngularVelocity > 0;

    // Simple peg hit detection: check if we're close to any peg angle
    for (const pegAngle of this.pegAngles) {
      if (this.hitPegs.has(pegAngle)) continue; // Skip already hit pegs

      // Check if current landing position is close to this peg
      let angleDiff = Math.abs(currentLandingDeg - pegAngle);
      if (angleDiff > 180) angleDiff = 360 - angleDiff; // Handle wrap-around

      // If we're within 2 degrees of a peg, consider it a hit
      if (angleDiff < 2.0) {
        // Mark this peg as hit
        this.hitPegs.add(pegAngle);

        // Play sound with random pitch variation
        this.pegHitSfx.play({ rate: Phaser.Math.FloatBetween(0.95, 1) });

        // Flip (clockwise wheel → counter‑clockwise flapper)
        const dir = isClockwise ? -1 : 1;
        this.emit("peg-cross", {
          centerDeg: pegAngle,
          speed: currentAngularVelocity,
          dir,
        });

        // Apply drag to physics body
        if (this.physicsBody) {
          this.physicsBody.setAngularVelocity(
            this.physicsBody.angularVelocity * this.pegDragFactor,
          );
        }

        // Only process one peg hit per frame to avoid audio spam
        break;
      }
    }
  }

  /**
   * Cancel all active animations and clear the array
   */
  private cancelAllAnimations(): void {
    for (const tween of this.activeTweens) {
      if (tween && !tween.isDestroyed()) {
        tween.destroy();
      }
    }
    this.activeTweens.length = 0;
    this.isAnimating = false;
  }

  private finishSpin() {
    this.lockWheel(true);
    this.rotation = Phaser.Math.Angle.Wrap(this.rotation);

    // Force stop braking and emit brake off event to stop shake animations
    if (this.isBraking) {
      this.isBraking = false;
      this.scene.game.events.emit(GameEvents.BREAK_WHEEL_OFF);
    }

    // Get current section based on rotation
    const selectedSection = this.getSelectedSection();

    // Triggers the calculation of the selected section
    if (selectedSection)
      Client.stateMachine.OnSpinCompleted(selectedSection.section);
  }

  private lockWheel(lock: boolean) {
    this.locked = lock;
    if (this.physicsBody) {
      this.physicsBody.setAngularDrag(lock ? 999999 : this.angularDrag);
    }
  }

  private createStarterFields(): void {
    const fieldSize = 360 / 10; // Assuming 8 sections for the wheel

    this.sections.push(
      new WheelSection(
        this,
        this.scene,
        10,
        fieldSize * 1.25,
        WheelSectionColors.RED,
      ),
      new WheelSection(
        this,
        this.scene,
        50,
        fieldSize,
        WheelSectionColors.GREEN,
      ),
      new WheelSection(
        this,
        this.scene,
        10,
        fieldSize * 1.25,
        WheelSectionColors.RED,
      ),
      new WheelSection(
        this,
        this.scene,
        50,
        fieldSize,
        WheelSectionColors.GREEN,
      ),
      new WheelSection(
        this,
        this.scene,
        10,
        fieldSize * 1.25,
        WheelSectionColors.RED,
      ),
      new WheelSection(
        this,
        this.scene,
        50,
        fieldSize,
        WheelSectionColors.GREEN,
      ),
      new WheelSection(
        this,
        this.scene,
        10,
        fieldSize * 1.25,
        WheelSectionColors.RED,
      ),
      new WheelSection(
        this,
        this.scene,
        250,
        fieldSize / 2,
        WheelSectionColors.YELLOW,
      ),
    );

    const bankruptcyField = new WheelSection(
      this,
      this.scene,
      0,
      fieldSize,
      WheelSectionColors.BLACK,
      "Bankruptcy",
    );
    bankruptcyField.effects.push(new BankruptcyEffect());
    this.sections.push(bankruptcyField);

    this.sections.push(
      new WheelSection(
        this,
        this.scene,
        250,
        fieldSize / 2,
        WheelSectionColors.YELLOW,
      ),
    );
  }

  /** Tell each section to draw itself and place it clockwise by array order. */
  public drawSections(): void {
    let currentAngleDeg = -90; // start at "12 o'clock"
    for (const section of this.sections) {
      section.draw(this.radius);
      section.setPosition(0, 0);
      // rotate the whole section so its local start (-90°) lands at currentAngleDeg
      section.setRotation(Phaser.Math.DegToRad(currentAngleDeg + 90));
      currentAngleDeg += section.fieldSize;
    }

    this.recomputePegs();
  }

  /** Recompute peg angles from all sections (degrees from 12 o'clock, clockwise). */
  public recomputePegs(): void {
    this.pegAngles.length = 0;
    let currentAngleDeg = -90; // start at "12 o'clock" (same as drawSections)

    for (const section of this.sections) {
      // Get all peg angles for this section
      const sectionPegAngles = section.getPegAngles(currentAngleDeg);

      // Convert from radians to degrees and add to our list
      for (const pegAngleRad of sectionPegAngles) {
        const pegAngleDeg = Phaser.Math.RadToDeg(pegAngleRad);
        this.pegAngles.push(pegAngleDeg);
      }

      currentAngleDeg += section.fieldSize;
    }

    // Normalize to [0,360) range
    this.pegAngles = this.pegAngles.map((a) => {
      let normalized = a % 360;
      if (normalized < 0) normalized += 360;
      return normalized;
    });

    this.pegAngles.sort((a, b) => a - b); // helpful for crossing checks
  }

  /**
   * Animate adding a new section to the wheel
   */
  private animateAddSection(
    newSection: WheelSection,
    insertPosition: number,
  ): void {
    this.isAnimating = true;

    // Calculate the center angle where the new section will be placed
    let targetAngle = -90; // Start at 12 o'clock
    for (let i = 0; i < insertPosition; i++) {
      targetAngle += this.sections[i].fieldSize;
    }
    targetAngle += newSection.fieldSize / 2; // Move to center of the new section

    // Convert to radians and calculate starting position (outside the wheel)
    const angleRad = Phaser.Math.DegToRad(targetAngle);
    const outsideRadius = this.radius * 2.5;
    const startX = Math.cos(angleRad) * outsideRadius;
    const startY = Math.sin(angleRad) * outsideRadius;

    // Position new section outside the wheel initially
    newSection.setPosition(startX, startY);
    newSection.setScale(0.2); // Start small
    newSection.setAlpha(0.3); // Start semi-transparent

    // First, redraw all sections to their new sizes (this creates the space)
    this.drawSections();

    // Animate new section moving in from outside
    const newSectionTween = this.scene.tweens.add({
      targets: newSection,
      x: 0,
      y: 0,
      scaleX: 1,
      scaleY: 1,
      alpha: 1,
      duration: this.animationDuration,
      ease: "Back.easeOut",
      onComplete: () => {
        this.isAnimating = false;
        // Final redraw to ensure everything is positioned correctly
        this.drawSections();
        this.recomputePegs();
      },
    });
    this.activeTweens.push(newSectionTween);
  }

  /**
   * Animate removing a section from the wheel
   */
  private animateRemoveSection(
    sectionToRemove: WheelSection,
    removeIndex: number,
    removedSize: number,
  ): void {
    this.isAnimating = true;

    // Calculate the center angle of the section being removed
    let currentAngle = -90; // Start at 12 o'clock
    for (let i = 0; i < removeIndex; i++) {
      currentAngle += this.sections[i].fieldSize;
    }
    currentAngle += sectionToRemove.fieldSize / 2; // Move to center of the section

    // Convert to radians and calculate outward direction
    const angleRad = Phaser.Math.DegToRad(currentAngle);
    const outsideRadius = this.radius * 2.5;
    const targetX = Math.cos(angleRad) * outsideRadius;
    const targetY = Math.sin(angleRad) * outsideRadius;

    // Step 1: Animate section moving out in its own direction
    const removeTween = this.scene.tweens.add({
      targets: sectionToRemove,
      x: targetX,
      y: targetY,
      scaleX: 0.1,
      scaleY: 0.1,
      alpha: 0,
      duration: this.animationDuration * 0.6,
      ease: "Power2.easeIn",
      onComplete: () => {
        // Step 2: Remove section and redistribute sizes
        sectionToRemove.destroy();
        this.sections.splice(removeIndex, 1);

        // Step 3: Animate sections resizing to fill the gap
        if (this.sections.length > 0) {
          const expandPerSection = removedSize / this.sections.length;

          // Store original sizes and calculate original positions
          const originalSizes: number[] = [];
          const originalPositions: number[] = [];

          // Calculate original positions before the section was removed
          // We need to reconstruct what the positions were before removal
          let tempAngle = -90;
          for (let i = 0; i < this.sections.length; i++) {
            originalSizes.push(this.sections[i].fieldSize);

            if (i < removeIndex) {
              // Section was before the removed section
              originalPositions.push(tempAngle);
              tempAngle += this.sections[i].fieldSize;
            } else {
              // Section was after the removed section - add the removed section's size to the gap
              if (i === removeIndex) {
                tempAngle += removedSize; // Account for the removed section's space
              }
              originalPositions.push(tempAngle);
              tempAngle += this.sections[i].fieldSize;
            }
          }

          // Calculate target positions after gap filling (normal sequential layout)
          const targetPositions: number[] = [];
          let targetAngle = -90; // Start at 12 o'clock

          for (let i = 0; i < this.sections.length; i++) {
            targetPositions.push(targetAngle);
            targetAngle += originalSizes[i] + expandPerSection; // New size after expansion
          }

          // Create an animation object to interpolate the resize and repositioning
          const animationProgress = { value: 0 };

          const resizeTween = this.scene.tweens.add({
            targets: animationProgress,
            value: 1,
            duration: this.animationDuration * 0.4,
            ease: "Power2",
            onUpdate: () => {
              // Interpolate section sizes and positions during animation
              for (let i = 0; i < this.sections.length; i++) {
                const originalSize = originalSizes[i];
                const targetSize = originalSize + expandPerSection;
                const originalPos = originalPositions[i];
                const targetPos = targetPositions[i];

                // Interpolate size
                this.sections[i].fieldSize =
                  originalSize +
                  (targetSize - originalSize) * animationProgress.value;

                // Interpolate position
                const currentPos =
                  originalPos +
                  (targetPos - originalPos) * animationProgress.value;

                // Draw section at interpolated position
                this.sections[i].draw(this.radius);
                this.sections[i].setPosition(0, 0);
                this.sections[i].setRotation(
                  Phaser.Math.DegToRad(currentPos + 90),
                );
              }

              // Update pegs
              this.recomputePegs();
            },
            onComplete: () => {
              this.isAnimating = false;
              // Final redraw to ensure exact sizes and positions
              this.drawSections();
              this.recomputePegs();
            },
          });
          this.activeTweens.push(resizeTween);
        } else {
          // Handle case where no sections remain
          this.isAnimating = false;
          this.drawSections();
          this.recomputePegs();
        }
      },
    });
    this.activeTweens.push(removeTween);
  }

  /**
   * Start braking the wheel
   */
  private startBraking(): void {
    if (!Client.state.wheelSpinning || this.brakeUsage >= this.maxBrakeUsage)
      return;
    this.isBraking = true;
  }

  /**
   * Stop braking the wheel
   */
  private stopBraking(): void {
    this.isBraking = false;
  }

  /**
   * Apply brake force to the wheel (3x stronger than peg brake)
   */
  private applyBrake(): void {
    if (!this.physicsBody) return;

    this.physicsBody.setAngularVelocity(
      Math.max(0, this.physicsBody.angularVelocity - this.brakePower),
    );
  }

  // ================================================================================================
  // DEBUG METHODS
  // ================================================================================================
  private setupDebugControls(): void {
    if (!this.scene.input.keyboard) return;

    this.leftKey = this.scene.input.keyboard.addKey(
      Phaser.Input.Keyboard.KeyCodes.LEFT,
    );
    this.rightKey = this.scene.input.keyboard.addKey(
      Phaser.Input.Keyboard.KeyCodes.RIGHT,
    );
    this.xKey = this.scene.input.keyboard.addKey(
      Phaser.Input.Keyboard.KeyCodes.X,
    );
    this.yKey = this.scene.input.keyboard.addKey(
      Phaser.Input.Keyboard.KeyCodes.Y,
    );

    console.warn("Debug controls enabled:");
    console.warn("* left/right arrows: manually rotate wheel");
    console.warn(
      `* X: remove random section (min: ${this.minNumberOfSections})`,
    );
    console.warn(`* Y: add random section (max: ${this.maxNumberOfSections})`);
  }

  /**
   * Get the wheel section that is currently underneath the detection point (12 o'clock position)
   * @returns Object containing the WheelSection and its index, or null if no section found
   */
  private getSelectedSection(): {
    section: WheelSection;
    index: number;
  } | null {
    if (this.sections.length === 0) return null;

    // Calculate the current detection point angle relative to the wheel's rotation
    // The detection point is at 12 o'clock (straight up), which is -90° in our coordinate system
    // We need to account for the wheel's current rotation
    const wheelRotationDeg = Phaser.Math.RadToDeg(this.rotation);

    // Calculate where the detection point "lands" on the wheel's coordinate system
    // This is the angle on the wheel that is currently at the 12 o'clock position
    const detectionAngle = (((-90 - wheelRotationDeg) % 360) + 360) % 360;

    // Convert to the same coordinate system used in drawSections (starting at -90°)
    // We need to find which section contains this angle
    let currentAngleDeg = -90; // Start at 12 o'clock, same as drawSections

    for (let i = 0; i < this.sections.length; i++) {
      const section = this.sections[i];
      const sectionStartAngle = currentAngleDeg;
      const sectionEndAngle = currentAngleDeg + section.fieldSize;

      // Normalize angles to [0, 360) for comparison
      const normalizedStart = ((sectionStartAngle % 360) + 360) % 360;
      const normalizedEnd = ((sectionEndAngle % 360) + 360) % 360;
      const normalizedDetection = ((detectionAngle % 360) + 360) % 360;

      // Check if detection angle falls within this section
      // Handle wrap-around case (section crosses 0°/360°)
      let isInSection = false;
      if (normalizedStart <= normalizedEnd) {
        // Normal case: section doesn't cross 0°
        isInSection =
          normalizedDetection >= normalizedStart &&
          normalizedDetection < normalizedEnd;
      } else {
        // Wrap-around case: section crosses 0°/360°
        isInSection =
          normalizedDetection >= normalizedStart ||
          normalizedDetection < normalizedEnd;
      }

      if (isInSection) {
        return { section, index: i };
      }

      currentAngleDeg += section.fieldSize;
    }

    // Fallback: if no section found (shouldn't happen), return the first section
    return { section: this.sections[0], index: 0 };
  }

  /**
   * Draw a single debug line showing the detection point (12 o'clock position)
   * This is the fixed point that collides with pegs as the wheel rotates
   */
  private drawDetectionPointLine(): void {
    // Clear existing debug graphics
    if (this.debugGraphics) {
      this.debugGraphics.destroy();
    }

    // Create new debug graphics
    this.debugGraphics = new Phaser.GameObjects.Graphics(this.scene);
    this.scene.add.existing(this.debugGraphics); // Add to scene, not to wheel container

    // Set high depth so it renders on top of everything
    this.debugGraphics.setDepth(1000);

    // Draw line from wheel center to 12 o'clock position (straight up)
    // Use wheel's world position since line is in scene coordinates
    const wheelCenterX = this.x;
    const wheelCenterY = this.y;
    const startX = wheelCenterX;
    const startY = wheelCenterY;
    const endX = wheelCenterX; // Straight up
    const endY = wheelCenterY - (this.radius + 60); // Extend beyond wheel edge

    // Draw the detection point line
    this.debugGraphics.lineStyle(4, 0xd442f5, 1); // Magenta line, 4px thick
    this.debugGraphics.beginPath();
    this.debugGraphics.moveTo(startX, startY);
    this.debugGraphics.lineTo(endX, endY);
    this.debugGraphics.strokePath();
  }

  private handleDebugControls(): void {
    if (!this.leftKey || !this.rightKey || !this.xKey || !this.yKey) return;

    // Handle section add/remove (only on key press, not hold)
    if (Phaser.Input.Keyboard.JustDown(this.xKey)) {
      const beforeCount = this.sections.length;
      // Remove section at random position
      const randomIndex = Math.floor(Math.random() * this.sections.length);
      this.removeSection(randomIndex, true); // Use animation
      if (this.sections.length < beforeCount) {
        console.log(
          `Removing section at position ${randomIndex} with animation. Sections remaining: ${this.sections.length}`,
        );
      }
    }

    if (Phaser.Input.Keyboard.JustDown(this.yKey)) {
      const beforeCount = this.sections.length;
      const colors = [
        WheelSectionColors.RED,
        WheelSectionColors.BLUE,
        WheelSectionColors.GREEN,
        WheelSectionColors.YELLOW,
      ];
      const randomColor = colors[Math.floor(Math.random() * colors.length)];
      const randomScore = Math.floor(Math.random() * 990) + 10;
      const randomPegs = Math.floor(Math.random() * 3) + 1;

      // Add section at random position
      const randomPosition = Math.floor(
        Math.random() * (this.sections.length + 1),
      );
      this.addSection(
        randomScore,
        30,
        randomColor,
        `${randomScore}`,
        [],
        randomPegs,
        randomPosition,
        true, // Use animation
      );
      if (this.sections.length > beforeCount) {
        console.log(
          `Adding new section with score ${randomScore} at position ${randomPosition} with animation. Total sections: ${this.sections.length}`,
        );
      }
    }

    // Handle rotation
    let debugRotation = 0;

    if (this.leftKey.isDown) {
      debugRotation = -this.debugRotationSpeed; // Rotate counter-clockwise
    } else if (this.rightKey.isDown) {
      debugRotation = this.debugRotationSpeed; // Rotate clockwise
    }

    if (debugRotation !== 0) {
      // Convert degrees to radians and apply rotation
      const rotationRad = Phaser.Math.DegToRad(debugRotation);
      this.rotation += rotationRad;

      // Simulate angular velocity in physics body for peg detection
      if (this.physicsBody) {
        this.physicsBody.setAngularVelocity(rotationRad * 60); // Convert to rad/s (assuming 60 FPS)
      }

      // Calculate landing position for peg detection
      const wheelRotationDeg = Phaser.Math.RadToDeg(this.rotation);
      const landingDeg = (((-90 - wheelRotationDeg) % 360) + 360) % 360;

      this.handlePegTicks(landingDeg);
    } else {
      // Reset angular velocity when not rotating
      if (this.physicsBody) {
        this.physicsBody.setAngularVelocity(0);
      }
    }
  }

  private isDebugRotating(): boolean {
    if (
      !Global.debug ||
      !this.leftKey ||
      !this.rightKey ||
      !this.xKey ||
      !this.yKey
    )
      return false;
    return (
      this.leftKey.isDown ||
      this.rightKey.isDown ||
      this.xKey.isDown ||
      this.yKey.isDown
    );
  }
}
